import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";
import { AuthProvider } from "@/contexts/AuthContext";
import { SyncProvider } from "@/contexts/SyncContext";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PermissionGate } from "@/components/auth/PermissionGate";
import { AppLayout } from "@/components/layout/AppLayout";
import { WorkOrderProvider } from "@/stores/workOrderStore";
import { SparePartsProvider } from "@/stores/sparePartsStore";
import { ReadinessProvider } from "@/stores/readinessStore";
import { DevDbBanner } from "@/components/DevDbBanner";

// Critical path — always bundled (fast nav from login)
import Login from "./pages/Login";
import KioskDisplay from "./pages/KioskDisplay";
import Dashboard from "./pages/Dashboard";
import WorkOrders from "./pages/WorkOrders";
import WorkOrderDetail from "./pages/WorkOrderDetail";

// Lazy-loaded — deferred until first navigation to that page
const Molds               = lazy(() => import("./pages/Molds"));
const MoldDetail          = lazy(() => import("./pages/MoldDetail"));
const ProductionReadiness = lazy(() => import("./pages/ProductionReadiness"));
const SpareParts          = lazy(() => import("./pages/SpareParts"));
const SparePartDetail     = lazy(() => import("./pages/SparePartDetail"));
const Reports             = lazy(() => import("./pages/Reports"));
const Settings            = lazy(() => import("./pages/Settings"));
const NotFound                = lazy(() => import("./pages/NotFound"));
const InjectDashboard         = lazy(() => import("./pages/inject/InjectionDashboard"));
const ProductionRuns          = lazy(() => import("./pages/inject/Production"));
const RunDetail               = lazy(() => import("./pages/inject/RunDetail"));
const InjectionMachines       = lazy(() => import("./pages/inject/InjectionMachines"));
const InjectionMachineDetail  = lazy(() => import("./pages/inject/InjectionMachineDetail"));
const InjectionReports        = lazy(() => import("./pages/inject/InjectionReports"));
const InjectionSettings       = lazy(() => import("./pages/inject/InjectionSettings"));
const Parts                   = lazy(() => import("./pages/inject/Parts"));
const PartDetail              = lazy(() => import("./pages/inject/PartDetail"));
const QualityDashboard        = lazy(() => import("./pages/quality/QualityDashboard"));
const NCList                  = lazy(() => import("./pages/quality/NCList"));
const NCNew                   = lazy(() => import("./pages/quality/NCNew"));
const NCDetail                = lazy(() => import("./pages/quality/NCDetail"));
const ScrapEntry              = lazy(() => import("./pages/quality/ScrapEntry"));
const ScrapHistory            = lazy(() => import("./pages/quality/ScrapHistory"));
const QualitySettings         = lazy(() => import("./pages/quality/QualitySettings"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <BrowserRouter>
        <AuthProvider>
          <SyncProvider>
          <WorkOrderProvider>
            <SparePartsProvider>
              <ReadinessProvider>
                <DevDbBanner />
                <Toaster />
                <Sonner />
                <Routes>
                  {/* Public */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/kiosk" element={<KioskDisplay />} />

                  {/* Root redirect */}
                  <Route path="/" element={<Navigate to="/maintenance" replace />} />

                  {/* ── Maintenance module ── */}
                  <Route
                    path="/maintenance/*"
                    element={
                      <ProtectedRoute>
                        <AppLayout>
                          <RouteErrorBoundary>
                          <Suspense fallback={null}>
                          <Routes>
                            <Route path=""                element={<Dashboard />} />
                            <Route path="work-orders"     element={<WorkOrders />} />
                            <Route path="work-orders/:id" element={<WorkOrderDetail />} />
                            <Route path="molds"           element={<Molds />} />
                            <Route path="molds/:id"       element={<MoldDetail />} />
                            <Route path="readiness"       element={<ProductionReadiness />} />
                            <Route path="spare-parts"     element={<SpareParts />} />
                            <Route path="spare-parts/:id" element={<SparePartDetail />} />
                            <Route path="reports"         element={<Reports />} />
                            <Route path="settings"        element={<Settings />} />
                            <Route path="*"               element={<NotFound />} />
                          </Routes>
                          </Suspense>
                          </RouteErrorBoundary>
                        </AppLayout>
                      </ProtectedRoute>
                    }
                  />

                  {/* ── Inject module ── */}
                  <Route
                    path="/inject/*"
                    element={
                      <ProtectedRoute>
                        <PermissionGate
                          permission="inject:view"
                          fallback={<Navigate to="/maintenance" replace />}
                        >
                          <AppLayout>
                            <RouteErrorBoundary>
                            <Suspense fallback={null}>
                            <Routes>
                              <Route path=""                element={<InjectDashboard />} />
                              <Route path="production"      element={<ProductionRuns />} />
                              <Route path="production/:id"  element={<RunDetail />} />
                              {/* Legacy /inject/runs preserved for any bookmarked links */}
                              <Route path="runs"            element={<Navigate to="/inject/production" replace />} />
                              <Route path="runs/:id"        element={<RunDetail />} />
                              <Route path="parts"           element={<Parts />} />
                              <Route path="parts/:id"       element={<PartDetail />} />
                              <Route path="machines"        element={<InjectionMachines />} />
                              <Route path="machines/:id"    element={<InjectionMachineDetail />} />
                              <Route path="reports"         element={<InjectionReports />} />
                              <Route path="settings"        element={<InjectionSettings />} />
                              <Route path="*"               element={<NotFound />} />
                            </Routes>
                            </Suspense>
                            </RouteErrorBoundary>
                          </AppLayout>
                        </PermissionGate>
                      </ProtectedRoute>
                    }
                  />

                  {/* ── Quality module ── */}
                  <Route
                    path="/quality/*"
                    element={
                      <ProtectedRoute>
                        <PermissionGate
                          permission="quality:view"
                          fallback={<Navigate to="/maintenance" replace />}
                        >
                          <AppLayout>
                            <RouteErrorBoundary>
                            <Suspense fallback={null}>
                            <Routes>
                              <Route path=""               element={<QualityDashboard />} />
                              <Route path="nc"             element={<NCList />} />
                              <Route path="nc/new"         element={<NCNew />} />
                              <Route path="nc/:id"         element={<NCDetail />} />
                              <Route path="scrap"          element={<ScrapEntry />} />
                              <Route path="scrap/history"  element={<ScrapHistory />} />
                              <Route path="settings"       element={<QualitySettings />} />
                              <Route path="*"              element={<NotFound />} />
                            </Routes>
                            </Suspense>
                            </RouteErrorBoundary>
                          </AppLayout>
                        </PermissionGate>
                      </ProtectedRoute>
                    }
                  />

                  {/* Catch-all */}
                  <Route path="*" element={<Navigate to="/maintenance" replace />} />
                </Routes>
              </ReadinessProvider>
            </SparePartsProvider>
          </WorkOrderProvider>
          </SyncProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
